import { createClient as createWSClient } from 'graphql-ws'
import {
  subscriptionExchange,
  subscriptionStore,
  getContextClient,
  cacheExchange,
  fetchExchange,
  mutationStore,
  queryStore,
  Client
} from '@urql/svelte'

/**
 * @param {{ agentApiKey?: string, url?: string|URL, sessionId?: string, initialState?: any, fetch?: function(any, any?): Promise<Response> }} params
 */
export function createClient({ agentApiKey, sessionId, fetch, url }) {
  const headers = {}
  if (agentApiKey) headers.authorization = `Bearer ${agentApiKey}`
  if (sessionId) {
    headers.session = sessionId
  } else if (typeof document !== 'undefined') {
    const session = document.cookie.match(/(?:^|;\s*)aa.session=(.+?)(?:;|$)/)
    if (session) headers.session = session[1]
  }

  const wsClient =
    typeof WebSocket !== 'undefined'
      ? createWSClient({
          lazyCloseTimeout: 10000,
          url: String(url).replace(/^http/, 'ws'),
          // @ts-ignore
          connectionParams: headers
        })
      : null

  const exchanges = [cacheExchange]

  if (!import.meta.env.SSR) {
    exchanges.push(
      fetchExchange,
      subscriptionExchange({
        enableAllOperations: true,
        forwardSubscription(request) {
          if (!wsClient) {
            throw new Error('WebSockets not supported in this environment')
          }
          return {
            subscribe(sink) {
              const payload = { query: '', ...request }
              const unsubscribe = wsClient.subscribe(payload, sink)
              return { unsubscribe }
            }
          }
        }
      })
    )
  }

  return new Client({
    url: String(url),
    exchanges,
    fetchOptions: {
      // @ts-ignore
      headers
    },
    fetch
  })
}

/**
 * @param {import('@urql/svelte').GraphQLRequestParams & { pause?: boolean, client?: import('@urql/svelte').Client }} params
 */
export function createQuery(params) {
  return queryStore({
    client: params.client || getContextClient(),
    ...params
  })
}

/** @type {function(string): App.Mutation<any>} */
export function createMutation(query) {
  const contextClient = getContextClient()
  return (variables, client = contextClient) =>
    /** @type {App.Operation<any>} */ (
      mutationStore({ client, query, variables })
    )
}

/**
 * @param {import('@urql/svelte').GraphQLRequestParams & { pause?: boolean, }} params
 */
export function createSubscription(params) {
  try {
    return subscriptionStore({
      client: getContextClient(),
      ...params
    })
  } catch (err) {
    if (
      err instanceof Error &&
      err.message ===
        'Expected operation of type "subscription" but found "query"'
    ) {
      // Fallback to query to work around type check in urql
      // https://github.com/urql-graphql/urql/discussions/2190#discussioncomment-9029525
      return createQuery(params)
    }
    throw err
  }
}
